/**
 * Component Registry for Amc Preferred Theme Theme
 * 
 * Extends the base preferred theme registry with Amc Preferred Theme specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-preferred/registry';
import { Hero } from './components/Hero';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { FeeGuide } from './components/FeeGuide';
import { AHScheduler } from './components/AHScheduler';
import { ScheduleATour } from './components/ScheduleATour';
import { ContactUs } from './components/ContactUs';
import { ApplyNow } from './components/ApplyNow';
import { ApartmentSyncSection } from './components/ApartmentSyncSection';
import { FloorplansList } from './components/FloorplansList';
import { SpecialOfferBanner } from './components/SpecialOfferBanner';
import { AccessibilityHero } from './components/AccessibilityHero';
import { AmenitiesHero } from './components/AmenitiesHero';
import { AmenitiesList } from './components/AmenitiesList';
import { ApplyNowHero } from './components/ApplyNowHero';
import { ContactHero } from './components/ContactHero';
import { FloorPlansHero } from './components/FloorPlansHero';
import { Gallery } from './components/Gallery';
import { GalleryHero } from './components/GalleryHero';
import { HyperlocalHero } from './components/HyperlocalHero';
import { InteractiveMapHero } from './components/InteractiveMapHero';
import { NeighborhoodHero } from './components/NeighborhoodHero';
import { PetPolicy } from './components/PetPolicy';
import { PetPolicyHero } from './components/PetPolicyHero';
import { PrivacyPolicyHero } from './components/PrivacyPolicyHero';
import { PropertyOverview } from './components/PropertyOverview';
import { ResidentAccessHero } from './components/ResidentAccessHero';
import { ResidentAccess } from './components/ResidentAccess';
import { ReviewsHero } from './components/ReviewsHero';
import { Reviews } from './components/Reviews';
import { ReviewsCallout } from './components/ReviewsCallout';
import { ScheduleATourHero } from './components/ScheduleATourHero';
import { SitemapHero } from './components/SitemapHero';
import { SitemapContent } from './components/SitemapContent';
import { ThankYouHero } from './components/ThankYouHero';
import { ThankYouContent } from './components/ThankYouContent';
import { ScheduleATourCallout } from './components/ScheduleATourCallout';
import { VirtualTourHero } from './components/VirtualTourHero';
import { VirtualTourEmbed } from './components/VirtualTourEmbed';

/**
 * Amc Preferred Theme specific component overrides.
 * These components have been customized for the Amc Preferred Theme brand.
 * 
 * Example:
 * import { Footer } from './components/Footer';
 * 
 * const amcpreferredthemeOverrides: Partial<ComponentRegistry> = {
 *   Footer,
 * };
 */
const amcpreferredthemeOverrides: Partial<ComponentRegistry> = {
  Hero,
  Header,
  Footer,
  FeeGuide,
  AHScheduler,
  ScheduleATour,
  ContactUs,
  ApplyNow,
  ApartmentSyncSection,
  FloorplansList,
  SpecialOfferBanner,
  AccessibilityHero,
  AmenitiesHero,
  AmenitiesList,
  ApplyNowHero,
  ContactHero,
  FloorPlansHero,
  Gallery,
  GalleryHero,
  HyperlocalHero,
  InteractiveMapHero,
  NeighborhoodHero,
  PetPolicy,
  PetPolicyHero,
  PrivacyPolicyHero,
  PropertyOverview,
  ResidentAccessHero,
  ResidentAccess,
  ReviewsHero,
  Reviews,
  ReviewsCallout,
  ScheduleATourHero,
  SitemapHero,
  SitemapContent,
  ThankYouHero,
  ThankYouContent,
  ScheduleATourCallout,
  VirtualTourHero,
  VirtualTourEmbed,
};

/**
 * Final merged registry for Amc Preferred Theme properties.
 * Inherits all components from the preferred theme, with Amc Preferred Theme overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...amcpreferredthemeOverrides,
};

export default registry;
